/**
 * @file   src\Pages\OrderDetails.tsx
 * @brief  Order Details page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, Container, Breadcrumb, Badge, Button, useOutletContext, useNavigate, useEffect, useState, useLocation, Col } from '../utils/thirdpartyComponents';
import { AppContextType } from '../interfaces/generalInterface';
import Deviceicon from '../assets/images/computer-chip.svg';
import CWSelect from '../components/CWSelect';
import Addresspinicon from '../assets/images/address-pin-icon.svg';
import { pageURLs } from '../utils/pageUrls';
import { userType } from '../utils/enums';
import axios from '../config/axiosConfig';
import { AdminOrderHardwareApi, OrderHardwareApi } from '../utils/apiUrls';
import { formatAddress } from '../components/FormatAddress';
import { formatDate, getBadgeClass, getFromLocalStorage, getOrderStatus } from '../utils/helper';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { MessageToaster } from '../utils/toastUtil';
import moment from 'moment';
import SmallLoader from '../components/SmallLoader';
import AssignDeviceModal from '../components/AssignDeviceModal';
import ReplaceDeviceModal from '../components/ReplaceDeviceModal';

const OrderDetails= (props: any) =>  {
    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false
    const shiplist= [
        { label: 'Ordered', value: '0'  },
        { label: 'Order Processing', value: '1' },
        { label: 'Shipped', value: '2' }, 
        { label: 'Delivered', value: '3' }
    ];
    const [selectedStatus, setSelectedStatus] = useState('');
    const [statusClassName,setStatusClassName] = useState('');
    const [loading, setLoading] = useState(false);
    const [isDeviceAssigned,setIsDeviceAssigned] = useState(false);
    const [selectedDeviceId,setSelectedDeviceId] = useState(0)
    const [selectedCompanyDeviceId,setSelectedCompanyDeviceId] = useState(0)

    // Instantiate MessageToaster
    const toaster = new MessageToaster();
    // Access the context
    const {  updateData } = useOutletContext<AppContextType>();
    const storageData = getFromLocalStorage('CARWASH_ADMIN_DATA');
    const userInfo = storageData && storageData !== null ? storageData : null;
    const navigate = useNavigate()
    // To get path from which redirection was done
    const location = useLocation();
    const origin = location.state?.origin || 'Unknown';
    const orderID = location.state?.orderID || '';
    const [orderDetailsData, setOrderDetails] = useState<any>([])
    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Date | null>>) => (date: Date | null) => {
        setter(date);
    };

    const [updatedDate, setUpdatedDate] = useState<Date | null>(null);
    const [assignedDevices,setAssignedDevices]=useState<[]>([]);
    
    // To display heading
    useEffect(() => {
        handleUpdate()
        getOrderDetails()
    }, [])

    useEffect(() => {
        getOrderDetails()
    }, [isDeviceAssigned]
    )

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Order Details", url: carwash_owner ? `/${pageURLs.order_history}` : `/superadmin/${pageURLs.order_list}`, showBackButton: origin === "MainMenu" ? false : true, showMainMenu: true, activeLink: "manage-hardware"});
    };


    const onStatusChange = (selectedOption: any) => {
        setSelectedStatus(selectedOption[0].value); // Update the selected status
        const className = getBadgeClass(selectedOption[0].value)
        setStatusClassName(className? className :"")
      };



    const getOrderDetails = async () => {
        try {
            setLoading(true);
            const { data } = (!carwash_owner) ? await axios.get(`${AdminOrderHardwareApi.HARDWARE_ORDER_DETAILS}/${orderID}`) : await axios.get(`${OrderHardwareApi.CARWASH_OWNER_HARDWARE_ORDER_DETAILS}/${orderID}`);

            if (data?.error === 0 ) {
                setOrderDetails(data?.data);
                if(data?.data?.deviceName)
                   
                { 
                    const devicestring = data?.data?.deviceName
                    const assigneddevices = devicestring.split(',');
                    setAssignedDevices(assigneddevices)
                }

                if (data?.data?.orderStatus !== undefined) {
                    const { orderStatus, createdOn, shippedDate, deliveredDate,orderProcessingDate} = data.data;
                    const dateMapping: { [key: number]: any }= {
                        0: createdOn,
                        1: orderProcessingDate,
                        2: shippedDate,
                        3: deliveredDate,
                    };
                    setUpdatedDate(dateMapping[orderStatus] || createdOn);
                }
                
            } 
            else{
                toaster.toastError(data?.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false);
        }
    };

    const updateOrderStatus = async () => {
        try{
            if(updatedDate==null)
            {
                toaster.toastError("Please select the date");
                return false;
            }
            else if(selectedStatus=="")
            {
                toaster.toastError("Please select status");
                return false;  
            }
            setLoading(true);
            let formatted_updatedDate = ""
            if (updatedDate) {
                formatted_updatedDate = moment(updatedDate).format('YYYY-MM-DD')
            }
            const updateOrderStatusResponse = await axios.post(AdminOrderHardwareApi.UPDATE_ORDER_STATUS, {
                "purchaseId": orderDetailsData?.purchaseID,
                "purchaseDtlsId": orderDetailsData?.purchaseDtlsID,
                "status": parseInt(selectedStatus),
                "statusDate": formatted_updatedDate
              })
              if (updateOrderStatusResponse?.data?.error === 0) {
                toaster.toastSuccess(updateOrderStatusResponse?.data?.message);
                navigate(`/superadmin/${pageURLs.order_list}`)
              }

        }
     catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
    } finally {
        setLoading(false);
    }
    }
    const [showAssignDeviceModal, setShowAssignDeviceModal] = useState(false);
    const handleCloseAssignDeviceModal = () => setShowAssignDeviceModal(false);
    const handleShowAssignDeviceModal = () => setShowAssignDeviceModal(true);

    const [replaceDeviceModal, setReplaceDeviceModal] = useState(false);

    const handleShowReplaceDeviceModal = (companyDeviceid : any, oldDeviceid : any) => {
        setReplaceDeviceModal(true);
        setSelectedDeviceId(oldDeviceid);
        setSelectedCompanyDeviceId(companyDeviceid)
    }
    const displayAssignedDevice = (device : string) => {
        const DeviceArray = device.split('||');
        const companyDeviceid = DeviceArray[2];
        const oldDeviceid = DeviceArray[1]
        return (<><div className="pill-item d-flex align-items-center text-center f-medium">{DeviceArray[0]}<Button variant="outline" className="btn-outline replace-btn fw-bold" onClick={ () => handleShowReplaceDeviceModal(companyDeviceid, oldDeviceid)} >Replace</Button></div></>)
    }
    //Close and fetch order status again after refresh
    const closeDeviceReplaceModalAfterSuccess = () => {
        setReplaceDeviceModal(false);
        setSelectedDeviceId(0);
        setSelectedCompanyDeviceId(0)
        getOrderDetails()
    }
    return (

        <div className='manage-carwash-container order-list-container'>
            <Container>
             <div className="nav-btn-title-sec d-flex justify-content-between align-items-center">
                   <Breadcrumb>
                   { (userInfo?.userTypeId == 1 || (userInfo?.userTypeId == 2 && userInfo?.permissionType == 1)) && <> 
                        <Breadcrumb.Item  onClick={()=>navigate(
                        "/superadmin/"+pageURLs.manage_hardware
                    )}> Manage Hardware</Breadcrumb.Item>
                    <Breadcrumb.Item  onClick={()=>navigate(
                        "/superadmin/"+pageURLs.order_list
                    )}> Order List</Breadcrumb.Item>
                        <Breadcrumb.Item active>Order Details</Breadcrumb.Item> </>}
                    </Breadcrumb> 
                    {/* <Button title="Track Order" variant="primary" className="primary-btn d-flex align-items-center track-btn" type="submit" onClick={()=>{carwash_owner ==true ? navigate("/"+pageURLs.track_order): navigate("/superadmin/"+pageURLs.track_order)}}>Track Order <TrackIcon/></Button> */}
                </div>
                {carwash_owner != true  && assignedDevices.length!=0 && <div className="manage-header-block d-flex justify-content-between align-items-center">
                     <div className="search-wrap order-search w-100 d-flex align-items-center">
                        <p className='f-semi'>Assigned Devices</p>
                            <div className="info-pills d-flex">
                                { assignedDevices?.map((device)=>(
                                    displayAssignedDevice(device)
                                ))
                                }
                            </div>
                    </div> 
                </div>}
                <div className="order-content-wrap d-flex flex-column position-relative">
                    <div className="order-detail-sec d-flex">
                        <div className="device-frame d-flex"><img src={Deviceicon} alt="Car Wash App" /></div>
                        <div className="content-sec">
                            <div className="order-info d-flex">
                                <div className="list-item">
                                    <p>Order Number</p>
                                    <h2 className='f-semi'>{orderDetailsData ? orderDetailsData?.orderID : ""}</h2>
                                </div>
                            </div>
                            <div className="ship-info d-flex align-items-center">
                            {carwash_owner != true ? 
                            
                            <CWSelect
                                    id="shipinfo"
                                    name="shipinfo"
                                    options={shiplist}
                                    values={shiplist.filter(option => parseInt(option.value) == orderDetailsData.orderStatus)}

                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select Status"
                                    multi={false}
                                    onChange={onStatusChange}
                                    className={statusClassName + " no-input"}
                                /> :
                                       orderDetailsData.orderStatus!=null && <Badge className={getBadgeClass(orderDetailsData.orderStatus)}>{getOrderStatus(orderDetailsData.orderStatus)}</Badge>}
                                         {carwash_owner != true ? <Col md={6}>
                                                <div className="form-group date-space">
                                                    {/* <p className="label">On</p> */}
                                                    <DatePicker
                                                        showIcon
                                                        selected={updatedDate}
                                                        onChange={handleDateChange(setUpdatedDate)}
                                                        placeholderText='Select Date'
                                                        maxDate={new Date()}
                                                        minDate={new Date(orderDetailsData?.createdOn)}
                                                    />
                                                </div>
                                            </Col>: <p>On <span className='f-semi'>{updatedDate ? " " + formatDate(updatedDate.toString()) : ""}</span></p>}
                                
                            </div>
                            <div className="info-pills d-flex">
                            {carwash_owner === true  && <div className="pill-item"><span className="f-semi">Device Name:</span> { orderDetailsData?.deviceName}</div>} 
                            {carwash_owner === false  && <div className="pill-item"><span className="f-semi">Device Name:</span> { orderDetailsData?.hardwareName}</div>} 
                                <div className="pill-item"><span className="f-semi">Quantity:</span> { orderDetailsData?.quanity}</div>
                                <div className="pill-item"><span className="f-semi">Amount:</span> ${ orderDetailsData?.price}</div>
                                
                                <div className="pill-item"><span className="f-semi">Ordered Date:</span> { formatDate(orderDetailsData?.createdOn)}</div>
                            </div>
                            { (carwash_owner != true && orderDetailsData?.deviceName==null && !isDeviceAssigned) ? <Button title="Assign Device" variant="primary" className="primary-btn assign-btn" onClick={handleShowAssignDeviceModal}>Assign Device</Button>:""}
                        </div>
                    </div>
                    <div className="address-sec d-flex">
                        <div className="address-block d-flex align-items-start">
                            <img src={Addresspinicon} alt="Car Wash App" />
                            <div className="address-info">
                                <h6 className='f-bold'>Billing Address</h6>
                               {orderDetailsData?.billingAddress ? formatAddress(orderDetailsData?.billingAddress):""} 
                            </div>
                        </div>
                        <div className="address-block d-flex align-items-start">
                            <img src={Addresspinicon} alt="Car Wash App" />
                            <div className="address-info">
                                <h6 className='f-bold'>Shipping Address</h6>
                                {orderDetailsData?.shippingAddress ? formatAddress(orderDetailsData?.shippingAddress): ""} 
                            </div>
                        </div>
                    </div>
                </div>
                {carwash_owner != true  && <div className="btn-sec d-flex justify-content-end">
                    <Button title="Save" variant="primary" className="primary-btn" type="submit" onClick={()=>updateOrderStatus()} >Save</Button>
                </div>}
            </Container >
            {loading && <SmallLoader />}

           { carwash_owner != true ? <AssignDeviceModal
                show={showAssignDeviceModal}
                onHide={handleCloseAssignDeviceModal}
                carWashId={orderDetailsData?.carWashID ? orderDetailsData?.carWashID : 0}
                purchaseDtlsId={orderDetailsData?.purchaseDtlsID ? orderDetailsData?.purchaseDtlsID : 0}
                quanity={orderDetailsData?.quanity}
                deviceType={orderDetailsData?.deviceType}
                onAssignSuccess={()=>setIsDeviceAssigned(true)}

            />:""}

            { (carwash_owner !== true && replaceDeviceModal) ? <ReplaceDeviceModal
                show={replaceDeviceModal}
                onHide={closeDeviceReplaceModalAfterSuccess}
                oldDeviceId = {selectedDeviceId}
                companyDeviceId = {selectedCompanyDeviceId}
                deviceType={orderDetailsData?.deviceType}
            />:""}
        </div >
    )
}

export default OrderDetails;
